import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeriesCard from "@/components/feature/SeriesCard";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { getSeriesById, seriesList } from "@/mocks/series";
import CharacterCard from "./components/CharacterCard";

export default function Detail() {
  const { id = "" } = useParams();
  const series = getSeriesById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: series
      ? `${series.title} - 仙剑奇侠传影视站剧情介绍`
      : "剧集不存在 - 仙剑奇侠传影视站",
    description: series
      ? `《${series.title}》剧情介绍、角色分析与制作信息。${series.summary[0].slice(0, 60)}`
      : "未找到对应的剧集内容，请返回分类页浏览仙侠剧集。",
    keywords: series
      ? `${series.title},${series.title}剧情,${series.title}角色,${series.tags.join(",")},仙侠剧`
      : "仙剑奇侠传,仙侠剧",
  });

  const recommendations = useMemo(() => {
    if (!series) return [];
    return seriesList
      .filter((item) => item.id !== series.id)
      .sort((a, b) => {
        const scoreA =
          (a.category === series.category ? 2 : 0) +
          a.tags.filter((tag) => series.tags.includes(tag)).length;
        const scoreB =
          (b.category === series.category ? 2 : 0) +
          b.tags.filter((tag) => series.tags.includes(tag)).length;
        if (scoreB !== scoreA) return scoreB - scoreA;
        return b.hot - a.hot;
      })
      .slice(0, 4);
  }, [series]);

  if (!series) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex min-h-[70vh] items-center justify-center px-4 pt-16 md:pt-20">
          <div className="text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-file-unknow-line text-3xl"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              内容不存在
            </h1>
            <p className="mt-3 max-w-md text-sm text-foreground-600">
              抱歉，没有找到你要查看的剧集，可能是链接有误或该内容已下架。你可以返回分类页浏览其他仙侠剧集。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-layout-grid-line"></i>
                返回分类页
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400"
              >
                <i className="ri-home-4-line"></i>
                回到首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const favorited = isFavorite(series.id);

  const handleFavorite = () => {
    const next = toggleFavorite(series.id);
    setToast(next ? "已加入收藏" : "已取消收藏");
    window.setTimeout(() => setToast(""), 2000);
  };

  const metaItems = [
    { label: "首播时间", value: `${series.year} 年`, icon: "ri-calendar-line" },
    { label: "集数", value: `全 ${series.episodes} 集`, icon: "ri-film-line" },
    { label: "地区", value: series.region, icon: "ri-map-pin-line" },
    { label: "副分类", value: series.category, icon: "ri-bookmark-3-line" },
    { label: "导演", value: series.director, icon: "ri-video-line" },
    { label: "编剧", value: series.writers, icon: "ri-edit-2-line" },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="relative overflow-hidden bg-primary-950">
          <img
            src={series.backdrop}
            alt={`${series.title} 剧照`}
            title={`${series.title} 主视觉剧照`}
            className="absolute inset-0 h-full w-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/40"></div>
          <div className="relative mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-primary-200">
              <Link to="/" className="hover:text-accent-400">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category" className="hover:text-accent-400">
                电视剧分类
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-background-50">{series.title}</span>
            </nav>

            <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end">
              <div className="h-64 w-44 shrink-0 overflow-hidden rounded-lg border-2 border-background-50/20 md:h-80 md:w-56">
                <img
                  src={series.poster}
                  alt={`${series.title} 海报`}
                  title={`${series.title} 剧集海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
                    {series.category}
                  </span>
                  {series.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-primary-100"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-5xl">
                  {series.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-primary-100">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-accent-400"></i>
                    {series.rating.toFixed(1)} 分
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-star-line"></i>
                    {series.cast.join(" · ")}
                  </span>
                </div>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to={`/play/${series.id}`}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    进入播放演示
                  </Link>
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-6 py-3 text-sm font-semibold transition-colors ${
                      favorited
                        ? "border-accent-500 bg-accent-500/15 text-accent-300"
                        : "border-background-50/50 text-background-50 hover:bg-background-50/10"
                    }`}
                  >
                    <i className={favorited ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"}></i>
                    {favorited ? "已收藏" : "收藏本剧"}
                  </button>
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回分类页
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {metaItems.map((item) => (
                <div key={item.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={item.icon}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-0.5 truncate text-sm font-medium text-foreground-900">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
            <div className="lg:col-span-2">
              <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
                <span className="h-6 w-1 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <div className="mt-6 space-y-5">
                {series.summary.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-sm leading-loose text-foreground-700 md:text-base md:leading-loose"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <h2 className="mt-12 flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
                <span className="h-6 w-1 rounded-full bg-accent-500"></span>
                角色详解
              </h2>
              <div className="-mx-4 mt-6 flex gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 lg:grid-cols-3">
                {series.characters.map((character) => (
                  <CharacterCard key={character.name} character={character} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg font-bold text-foreground-950">主演阵容</h3>
                <ul className="mt-4 space-y-3">
                  {series.cast.map((actor, index) => (
                    <li key={actor} className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-semibold text-background-50">
                        {index + 1}
                      </span>
                      <span className="text-sm text-foreground-800">{actor}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 border-t border-background-300 pt-5">
                  <p className="text-xs leading-relaxed text-foreground-600">
                    本页内容为演示数据，仅用于展示网站功能与结构，剧情与角色介绍不作为官方资料。
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
            <div className="flex items-end justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-accent-700">
                  <span className="h-px w-6 bg-accent-500"></span>
                  More like this
                </span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  相关推荐剧集
                </h2>
              </div>
              <Link
                to="/category"
                className="hidden items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-700 hover:underline sm:inline-flex"
              >
                浏览全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
              {recommendations.map((item) => (
                <SeriesCard key={item.id} series={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50">
          {toast}
        </div>
      )}

      <Footer />
    </div>
  );
}